import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import axios from "axios";
import apiUrls from "../../../api";
import PlatformIcon from "../../../common/components/Icon/PlatformIcon/PlatformIcon";
import {
  DownloadIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import { store } from "../../../common/state/store";
import {
  getConnectedDevice,
  getInstalledPackages,
  setEnduserBuilds,
} from "../../deviceInstaller/state/deviceInstallerSlice";
import { isApkPlatform, detectDeviceFamily } from "../../deviceInstaller/utils/platformCompat";
import {
  getBuildInstallStatus,
  mapToEnduserBuildInfos,
} from "../../deviceInstaller/utils/buildStatus";
import { installApkFromUrl } from "../../deviceInstaller/utils/adbInstaller";
import { requestAndConnect, disconnect } from "../../deviceInstaller/utils/adbConnection";
import { isWebUsbSupported } from "../../deviceInstaller/utils/browserCompat";
import InstallProgressModal from "../../deviceInstaller/components/InstallProgressModal";
import UninstallButton from "../../deviceInstaller/components/UninstallButton";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import {
  DeviceIcon,
  CheckIcon,
  WarningCircleIcon,
  DownloadArrowIcon,
  Spinner,
} from "../../deviceInstaller/components/icons";
import { trackEvent } from "../../../trackEvent";

interface IBuildDownload {
  id: number;
  name: string | null;
  targetPlatform: string;
  version: string;
  downloadUrl: string;
  sizeInBytes: number | null;
  packageName: string | null;
}

interface EnduserDownloadsSectionProps {
  projectGroupId: number;
  projectGroupName?: string;
}

const DOWNLOADS_ACKNOWLEDGED_KEY = "wixar_downloads_acknowledged";

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/** A single build card with download + optional device install */
function BuildCard({ build }: { build: IBuildDownload }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);
  const installedPackages = useAppSelector(getInstalledPackages);
  const [showModal, setShowModal] = useState(false);
  // Persists across modal close — survives clearInstallJob
  const [installSucceeded, setInstallSucceeded] = useState(false);

  const isDeviceReady = device?.status === "ready";
  const isApk = isApkPlatform(build.targetPlatform);
  const deviceFamily = device ? detectDeviceFamily(device.productName) : null;

  const status = getBuildInstallStatus({
    isDeviceReady: !!isDeviceReady && isApk,
    deviceFamily,
    targetPlatform: build.targetPlatform,
    packageName: build.packageName,
    buildVersion: Number(build.version),
    installedPackages,
  });

  // Fallback: local flag set when installApkFromUrl resolved successfully
  const effectiveStatus = status === "install" && installSucceeded ? "installed" : status;

  const deviceVersionCode = build.packageName
    ? installedPackages[build.packageName]?.versionCode
    : null;

  const handleInstall = async () => {
    setShowModal(true);
    setInstallSucceeded(false);
    const fileName = build.name || `wixar_build_${build.id}.apk`;
    await installApkFromUrl(
      build.downloadUrl,
      fileName,
      build.id,
      dispatch,
      build.packageName ?? undefined,
    );
    const jobStatus = store.getState().deviceInstaller.installJob?.status;
    if (jobStatus === "success") {
      setInstallSucceeded(true);
    }
  };

  const showDeviceActions = isDeviceReady && isApk && effectiveStatus !== "not_connected";

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg min-w-[140px]">
      {/* Top line: icon + platform + version */}
      <div className="flex items-center gap-2">
        <PlatformIcon
          platform={build.targetPlatform}
          iconProps={{ width: "24px", height: "24px", fill: "#374151" }}
        />
        <span className="text-xs font-medium text-gray-700">v{build.version}</span>
        {build.sizeInBytes && (
          <span className="text-[10px] text-gray-400">{formatFileSize(build.sizeInBytes)}</span>
        )}
      </div>

      {/* Device status + actions */}
      {showDeviceActions && effectiveStatus === "incompatible" && (
        <Tooltip message={t("enduserStats.downloads.incompatible")} classNameChildren="left-0">
          <span className="text-[10px] text-gray-400 italic">
            {t("enduserStats.downloads.incompatible")}
          </span>
        </Tooltip>
      )}

      {showDeviceActions && effectiveStatus === "install" && (
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-white bg-green-500 hover:opacity-90 transition-colors w-fit"
        >
          <DeviceIcon className="w-3.5 h-3.5" />
          {t("enduserStats.downloads.installOnDevice")}
        </button>
      )}

      {showDeviceActions && effectiveStatus === "installed" && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-xs text-gray-600">
            <CheckIcon className="w-3.5 h-3.5 text-green-500" />
            {t("enduserStats.downloads.installedVersion", { version: build.version })}
          </span>
          {build.packageName && (
            <UninstallButton
              packageName={build.packageName}
              className="px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-200 rounded transition-colors"
              onUninstalled={() => setInstallSucceeded(false)}
            />
          )}
        </div>
      )}

      {showDeviceActions && effectiveStatus === "outdated" && (
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-xs text-orange-600">
            <WarningCircleIcon className="w-3 h-3" />
            {t("enduserStats.downloads.installedVersion", { version: deviceVersionCode })}
          </span>
          <button
            onClick={handleInstall}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-white bg-green-500 hover:opacity-90 transition-colors w-fit"
          >
            <DownloadArrowIcon />
            {t("enduserStats.downloads.updateOnDevice", { version: build.version })}
          </button>
        </div>
      )}

      {/* Post-install hint */}
      {installSucceeded && effectiveStatus === "installed" && (
        <span className="text-[10px] text-gray-500 italic">
          {t("enduserStats.downloads.postInstallHint")}
        </span>
      )}

      {/* Download link — always present, secondary when device actions are shown */}
      <a
        href={build.downloadUrl}
        download
        onClick={() => trackEvent("enduser-build-download")}
        className={`inline-flex items-center gap-1 text-xs transition-colors ${
          showDeviceActions && effectiveStatus !== "incompatible"
            ? "text-gray-400 hover:text-gray-600"
            : "text-gray-600 hover:text-green-700"
        }`}
        title={`${build.targetPlatform} v${build.version}${
          build.sizeInBytes ? ` (${formatFileSize(build.sizeInBytes)})` : ""
        }`}
      >
        <DownloadIcon className="h-3.5 w-3.5" />
        {t("enduserStats.downloads.downloadFallback")}
      </a>

      {showModal && <InstallProgressModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

/** Compact device connection banner inside the downloads section */
function CompactConnectionBanner() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);

  if (!isWebUsbSupported()) return null;

  const isConnecting = device?.status === "connecting";
  const isAuthenticating = device?.status === "authenticating";
  const isReady = device?.status === "ready";

  if (isReady) return null;

  const handleConnect = async () => {
    await requestAndConnect(dispatch);
  };

  if (isConnecting || isAuthenticating) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md text-sm mb-2">
        <Spinner color="green" className="w-3 h-3 flex-shrink-0" />
        <span className="text-gray-600">
          {isAuthenticating
            ? t("deviceInstaller.authInstructions")
            : t("deviceInstaller.deviceConnecting")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md text-sm mb-2">
      <svg
        className="w-4 h-4 text-gray-400 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101"
        />
      </svg>
      <span className="text-gray-500">{t("enduserStats.downloads.connectPrompt")}</span>
      <button
        onClick={handleConnect}
        className="font-medium text-green-500 hover:opacity-80 transition-opacity"
      >
        {t("enduserStats.downloads.connectAction")}
      </button>
      <span className="text-gray-400">{t("enduserStats.downloads.connectSuffix")}</span>
    </div>
  );
}

/** Inline connected device indicator with disconnect */
function ConnectedDeviceIndicator() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);

  if (!device || device.status !== "ready") return null;

  const handleDisconnect = async () => {
    await disconnect(dispatch);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 rounded-full flex-shrink-0 bg-green-500" />
      <span className="text-xs font-medium text-green-500">{device.productName}</span>
      <button
        onClick={handleDisconnect}
        className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
      >
        {t("deviceInstaller.disconnect")}
      </button>
    </div>
  );
}

const EnduserDownloadsSection = ({
  projectGroupId,
  projectGroupName,
}: EnduserDownloadsSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);
  const [builds, setBuilds] = useState<IBuildDownload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAcknowledged, setIsAcknowledged] = useState(() => {
    return localStorage.getItem(DOWNLOADS_ACKNOWLEDGED_KEY) === "true";
  });

  const isDeviceReady = device?.status === "ready";

  const handleAcknowledge = () => {
    setIsAcknowledged(true);
    localStorage.setItem(DOWNLOADS_ACKNOWLEDGED_KEY, "true");
  };

  useEffect(() => {
    const fetchBuilds = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${apiUrls.stats.enduserProjectGroupBuilds}?projectGroupId=${projectGroupId}`,
        );
        const fetchedBuilds: IBuildDownload[] = response.data || [];
        setBuilds(fetchedBuilds);
        setError(null);

        // Register builds in shared state for DeviceManager
        const enduserBuildMap = mapToEnduserBuildInfos(
          fetchedBuilds.map((b) => ({
            ...b,
            version: b.version,
            projectGroupName: projectGroupName || `PG #${projectGroupId}`,
          })),
        );
        if (Object.keys(enduserBuildMap).length > 0) {
          dispatch(setEnduserBuilds(enduserBuildMap));
        }
      } catch (err) {
        console.error("Failed to fetch builds:", err);
        setError("Failed to load downloads");
      } finally {
        setIsLoading(false);
      }
    };

    if (projectGroupId) {
      void fetchBuilds();
    }
  }, [projectGroupId, projectGroupName, dispatch]);

  if (!isLoading && builds.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 w-20 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <DownloadIcon className="h-4 w-4 text-gray-500" />
            {isDeviceReady
              ? t("enduserStats.downloads.titleWithDevice", { name: device.productName })
              : t("enduserStats.downloads.title")}
          </h3>
          {isDeviceReady && <ConnectedDeviceIndicator />}
        </div>
        <div className="relative group">
          <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
          <div className="absolute right-0 top-6 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            <p className="mb-2">{t("enduserStats.downloads.helpText")}</p>
            <p className="text-gray-300">{t("enduserStats.downloads.contactAdmin")}</p>
          </div>
        </div>
      </div>

      {!isAcknowledged ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <InformationCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">{t("enduserStats.downloads.warningTitle")}</p>
              <p className="text-gray-600">{t("enduserStats.downloads.warningText")}</p>
              <p className="text-gray-500 mt-2">{t("enduserStats.downloads.contactAdmin")}</p>
            </div>
          </div>
          <button
            onClick={handleAcknowledge}
            className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 transition-colors group"
          >
            <ChevronDownIcon className="h-4 w-4 text-green-500 group-hover:text-green-600 transition-transform group-hover:translate-y-0.5" />
            <span>{t("enduserStats.downloads.acknowledge")}</span>
          </button>
        </div>
      ) : (
        <div>
          {!isDeviceReady && <CompactConnectionBanner />}
          <div className="flex flex-wrap gap-2">
            {builds.map((build) => (
              <BuildCard key={build.id} build={build} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnduserDownloadsSection;
